import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw, AlertTriangle, CalendarDays, Zap } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const DEFAULT_SPECIAL_CODES = ["PTO", "P", "L", "H", "A", "U"];

export default function ResetAllDialog({
  open, onClose, facilityId, facilityLabel, payPeriodId, payPeriodLabel, cadence, codes, onDone,
}) {
  const isMonthly = String(payPeriodId || "").startsWith("mo-") || cadence === "monthly";
  const specials = useMemo(() => {
    if (!codes) return DEFAULT_SPECIAL_CODES.map(c => ({ code: c, label: c }));
    const nonWork = codes.filter(c => c.type && c.type !== "work");
    const merged = new Map();
    nonWork.forEach(c => merged.set(c.code, { code: c.code, label: c.label || c.code, color: c.color }));
    DEFAULT_SPECIAL_CODES.forEach(c => {
      if (!merged.has(c)) {
        const m = codes.find(x => x.code === c);
        if (m) merged.set(c, { code: c, label: m.label || c, color: m.color });
      }
    });
    if (merged.size === 0) DEFAULT_SPECIAL_CODES.forEach(c => merged.set(c, { code: c, label: c }));
    return Array.from(merged.values());
  }, [codes]);

  const [overwrite, setOverwrite] = useState({});
  const [busy, setBusy] = useState(false);

  // Bulk-future mode (admin power tool)
  const [bulkFuture, setBulkFuture] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [periodsAhead, setPeriodsAhead] = useState(12);

  useEffect(() => {
    if (open) {
      setOverwrite({});
      setBulkFuture(false);
      setPeriodsAhead(12);
      try {
        const anchor = payPeriodId?.startsWith("pp-") ? payPeriodId.slice(3) : format(new Date(), "yyyy-MM-dd");
        setStartDate(anchor);
      } catch { setStartDate(format(new Date(), "yyyy-MM-dd")); }
    }
  }, [open, payPeriodId]);

  const toggle = (code) => setOverwrite((s) => ({ ...s, [code]: !s[code] }));
  const setAll = (val) => setOverwrite(Object.fromEntries(specials.map(s => [s.code, val])));

  const submit = async () => {
    const protect_codes = specials.filter(s => !overwrite[s.code]).map(s => s.code);
    setBusy(true);
    try {
      if (bulkFuture) {
        const { data } = await api.post("/schedule/reset-future", {
          facility_id: facilityId, start_date: startDate,
          periods_ahead: Number(periodsAhead) || 12, protect_codes,
        });
        toast.success(
          `Refreshed ${data.periods} pay periods · ${data.touched_employees} employees · ${data.applied} shifts applied, ${data.protected} protected, ${data.cleared} cleared`
        );
      } else if (isMonthly && String(payPeriodId).startsWith("mo-")) {
        const { data } = await api.post("/schedule/reset-month", {
          facility_id: facilityId, month_id: payPeriodId, protect_codes,
        });
        toast.success(
          `Refreshed ${data.periods} pay period(s) in this month · ${data.touched_employees} employees · ${data.applied} shifts applied, ${data.protected} protected, ${data.cleared} cleared`
        );
      } else {
        const { data } = await api.post("/schedule/reset-all", {
          facility_id: facilityId, pay_period_id: payPeriodId, protect_codes,
        });
        toast.success(
          `Refreshed ${data.touched_employees} employees — ${data.applied} shifts applied, ${data.protected} protected, ${data.cleared} cleared`
        );
      }
      onDone && onDone(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to refresh");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="reset-all-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <RefreshCw size={18} className="text-blue-600" /> Refresh schedule
          </DialogTitle>
          <DialogDescription>
            {bulkFuture
              ? <>Bulk refresh <b>every pay period</b> from a start date forward at <b>{facilityLabel || facilityId}</b>.</>
              : <>Apply master patterns to <b>{payPeriodLabel || payPeriodId}</b> at <b>{facilityLabel || facilityId}</b>.</>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Bulk-future toggle — admin power tool */}
          <label className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition-colors ${
            bulkFuture ? "border-fuchsia-400 bg-fuchsia-50" : "border-slate-200 bg-white hover:bg-slate-50"
          }`} data-testid="reset-bulk-toggle">
            <Checkbox
              checked={bulkFuture}
              onCheckedChange={(v) => setBulkFuture(!!v)}
              className="mt-0.5"
              data-testid="reset-bulk-cb"
            />
            <div className="flex-1">
              <div className="font-semibold text-sm flex items-center gap-1.5">
                <Zap size={13} className="text-fuchsia-600" /> Bulk future refresh (admin)
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Apply the master template to every pay period starting from a chosen date — one click, no need to visit each period.
              </div>
            </div>
          </label>

          {bulkFuture && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs flex items-center gap-1"><CalendarDays size={11}/> Start from</Label>
                <Input
                  type="date" value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="reset-start-date"
                />
              </div>
              <div>
                <Label className="text-xs">Number of pay periods</Label>
                <Input
                  type="number" min={1} max={26}
                  value={periodsAhead}
                  onChange={(e) => setPeriodsAhead(e.target.value)}
                  data-testid="reset-periods-ahead"
                />
                <div className="text-[10px] text-slate-400 mt-0.5">2 weeks each · max 26 (~1 year)</div>
              </div>
            </div>
          )}

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900 flex items-start gap-1.5">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <div>
              Tick the codes you <b>want overwritten</b>. Anything un-ticked stays put — for example leave PTO un-ticked and every approved PTO across all refreshed periods is preserved.
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-slate-500">Overwrite these codes</div>
              <div className="flex gap-2">
                <button type="button" className="text-[11px] text-blue-600 hover:underline" onClick={() => setAll(true)} data-testid="reset-all-tick">
                  Select all
                </button>
                <button type="button" className="text-[11px] text-slate-500 hover:underline" onClick={() => setAll(false)} data-testid="reset-all-untick">
                  Protect all
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {specials.map((s) => (
                <label
                  key={s.code}
                  className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 cursor-pointer transition-colors ${
                    overwrite[s.code] ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                  data-testid={`reset-all-code-${s.code}`}
                >
                  <Checkbox
                    checked={!!overwrite[s.code]}
                    onCheckedChange={() => toggle(s.code)}
                    data-testid={`reset-all-cb-${s.code}`}
                  />
                  <div className="min-w-0">
                    <div className="text-xs font-bold tabular-nums">{s.code}</div>
                    <div className="text-[10px] text-slate-500 truncate">{s.label}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-slate-500">
            Regular work shifts are always overwritten. Long-lunch notes and coverage assignments on refreshed cells are cleared.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={submit} disabled={busy} data-testid="reset-all-submit">
            {busy ? "Refreshing…" : (bulkFuture ? "Refresh all future periods" : (isMonthly && String(payPeriodId).startsWith("mo-") ? "Refresh this month" : "Refresh this period"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
